import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { getBaseLanguage } from "@/lib/i18n";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import profileImage from "@/assets/profile-delfu.jpg";
import { BRAND_HANDLE, BRAND_PERSON_NAME } from "@/lib/brandConfig";

const TALLY_EMBED_URL =
  "https://consulta.delfuocampo.com/?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1&formEventsForwarding=1&embed=1";
const TALLY_SCRIPT = "https://tally.so/widgets/embed.js";

const Contacto = () => {
  useSEO("contacto");
  const { language } = useLanguage();
  const isEs = getBaseLanguage(language) === "es";

  useEffect(() => {
    const hydrate = () => {
      const w = window as unknown as { Tally?: { loadEmbeds: () => void } };
      if (typeof w.Tally !== "undefined") {
        w.Tally.loadEmbeds();
      }
      // Fallback SPA: set src manually for any iframe not yet hydrated
      document
        .querySelectorAll<HTMLIFrameElement>("iframe[data-tally-src]:not([src])")
        .forEach((iframe) => {
          const src = iframe.dataset.tallySrc;
          if (src) iframe.src = src;
        });
    };

    if (!document.querySelector(`script[src="${TALLY_SCRIPT}"]`)) {
      const s = document.createElement("script");
      s.src = TALLY_SCRIPT;
      s.async = true;
      s.onload = hydrate;
      s.onerror = hydrate;
      document.body.appendChild(s);
    } else {
      hydrate();
    }

    const onMessage = (e: MessageEvent) => {
      if (typeof e.data !== "string" || !e.data.includes("Tally.")) return;
      let evt: string | undefined;
      try {
        evt = (JSON.parse(e.data) as { event?: string }).event;
      } catch {
        return;
      }
      if (!evt) return;
      const ph = (
        window as unknown as {
          posthog?: { capture?: (n: string, p?: Record<string, unknown>) => void };
        }
      ).posthog;
      const base = { surface: "contacto_page", template: "Contacto" };
      if (evt.includes("PageView") || evt.includes("Loaded")) {
        ph?.capture?.("contact_form_viewed", base);
      } else if (evt.includes("Submit")) {
        ph?.capture?.("contact_form_submitted", base);
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-lg mx-auto px-5 py-12 md:py-16">
        <header className="text-center mb-10 animate-fade-in">
          <div className="relative inline-block mb-6">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-border via-muted to-border opacity-60" />
            <div className="relative w-20 h-20 rounded-full overflow-hidden ring-2 ring-background shadow-lg">
              <img
                src={profileImage}
                alt={`${BRAND_PERSON_NAME} - contacto`}
                loading="eager"
                width={80}
                height={80}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <h1 className="text-2xl md:text-3xl font-display font-medium text-foreground tracking-wide mb-2 italic">
            {isEs ? "Contacto" : "Contact"}
          </h1>
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="w-6 h-px bg-border" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
              {BRAND_PERSON_NAME}
            </p>
            <span className="w-6 h-px bg-border" />
          </div>
          <p className="text-sm text-muted-foreground max-w-xs mx-auto">
            {isEs
              ? "Colaboraciones, campañas y consultas de marca"
              : "Collaborations, campaigns and brand partnerships"}
          </p>
        </header>

        {/* Tally embed */}
        <section className="animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <iframe
            data-tally-src={TALLY_EMBED_URL}
            width="100%"
            height="400"
            frameBorder="0"
            marginHeight={0}
            marginWidth={0}
            title={isEs ? "Formulario de contacto" : "Contact form"}
            className="rounded-lg min-h-[400px]"
          />
        </section>

        <div className="text-center mt-10 animate-fade-in" style={{ animationDelay: "0.2s" }}>
          <Link
            to="/"
            className="inline-block text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-foreground/20 rounded"
          >
            ← {isEs ? "Volver al inicio" : "Back to home"}
          </Link>
        </div>

        <footer className="mt-10 text-center animate-fade-in" style={{ animationDelay: "0.3s" }}>
          <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/60">
            {`@${BRAND_HANDLE}`}
          </p>
        </footer>
      </main>
    </div>
  );
};

export default Contacto;
